/**
 * MemoryExportModal — "Copy memory prompt". Pick any of the memories (all by
 * default), see the exact prompt, then copy it for another AI assistant or
 * download it as a .md file. Nothing touches the server.
 */
import { useState } from "react";
import { Check, Copy, Download } from "lucide-react";
import { cn } from "@/utils/helpers";
import { queue } from "@/components/Toast";
import type { MemoryBase } from "@/app/routes/dashboard/settings/api/memoryMutations";
import {
  downloadTextFile,
  type MemoryExportFormat,
} from "@/app/routes/dashboard/settings/memoryExport";
import { KitButton } from "../../kit/Button";
import { kitBareFieldClass } from "../../kit/Field";
import { KitCheckbox } from "../../kit/Checkbox";
import { KitModal } from "../../kit/Modal";

export default function MemoryExportModal<M extends MemoryBase>({
  memories,
  format,
  onClose,
}: {
  memories: M[];
  format: MemoryExportFormat;
  onClose: () => void;
}) {
  const [excluded, setExcluded] = useState<Set<string>>(() => new Set());
  const [copied, setCopied] = useState(false);

  const picked = memories.filter((m) => !excluded.has(m.id));
  const bodies = picked.map((m) => m.body);
  const prompt = format.prompt(bodies);
  const allPicked = picked.length === memories.length;

  const toggle = (id: string, selected: boolean) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (selected) next.delete(id);
      else next.add(id);
      return next;
    });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      queue.add({
        title: "Could not copy",
        description: "Your browser blocked the clipboard. Select the text and copy it instead.",
        variant: "error",
      });
    }
  };

  return (
    <KitModal
      isOpen
      onOpenChange={(next) => (next ? undefined : onClose())}
      title="Copy memory prompt"
      description="Paste it into another AI assistant so it works with the same memories. Untick anything you don't want to share."
      modalClassName="!w-[min(calc(100vw-2rem),640px)] !max-w-[640px]"
    >
      <div className="flex w-full flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-co-faint">
            {picked.length} of {memories.length} included
          </span>
          <button
            type="button"
            onClick={() => setExcluded(allPicked ? new Set(memories.map((m) => m.id)) : new Set())}
            className="text-[12px] text-co-muted underline decoration-co-line2 underline-offset-4 transition-colors hover:text-co-text"
          >
            {allPicked ? "Select none" : "Select all"}
          </button>
        </div>

        <ul className="flex max-h-56 flex-col gap-1.5 overflow-y-auto">
          {memories.map((m) => {
            const selected = !excluded.has(m.id);
            return (
              <li
                key={m.id}
                className={cn(
                  "rounded-card border bg-co-surface px-3 py-2 transition-colors",
                  selected ? "border-co-accent-border" : "border-co-line2",
                )}
              >
                <KitCheckbox
                  isSelected={selected}
                  onChange={(next) => toggle(m.id, next)}
                  className="items-start"
                >
                  <span className={cn("text-[13px] leading-relaxed", selected ? "text-co-text" : "text-co-muted")}>
                    {m.body}
                  </span>
                </KitCheckbox>
              </li>
            );
          })}
        </ul>

        <textarea
          readOnly
          value={picked.length > 0 ? prompt : ""}
          placeholder="Pick at least one memory."
          rows={8}
          aria-label="Memory prompt"
          onFocus={(e) => e.currentTarget.select()}
          className={cn(kitBareFieldClass, "block w-full resize-none px-3 py-2 font-mono text-[12px] leading-relaxed text-co-text2")}
        />

        <div className="flex gap-3 pt-1">
          <KitButton
            type="button"
            variant="secondary"
            iconLeft={Download}
            onClick={() => downloadTextFile(format.fileName, format.markdown(bodies))}
            isDisabled={picked.length === 0}
            className="flex-1"
          >
            Download .md
          </KitButton>
          <KitButton
            type="button"
            iconLeft={copied ? Check : Copy}
            onClick={() => void copy()}
            isDisabled={picked.length === 0}
            className="flex-1"
          >
            {copied ? "Copied" : "Copy prompt"}
          </KitButton>
        </div>
      </div>
    </KitModal>
  );
}
